import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Trash2, Check, PenLine } from 'lucide-react';
import { motion } from 'framer-motion';

const SignatureCanvas = ({ onSignatureChange, label = '', height = 140, required = true }) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const lastPos = useRef(null);
  const confirmedRef = useRef(false);
  const isDrawingRef = useRef(false);
  const hasDrawnRef = useRef(false);

  confirmedRef.current = confirmed;
  isDrawingRef.current = isDrawing;
  hasDrawnRef.current = hasDrawn;

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = height * dpr;
    canvas.style.height = `${height}px`;
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, rect.width, height);
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 0.8;
    ctx.setLineDash([5, 5]);
    ctx.beginPath();
    ctx.moveTo(20, height * 0.72);
    ctx.lineTo(rect.width - 20, height * 0.72);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = '#bbb';
    ctx.font = '13px IBM Plex Sans, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Sign here', rect.width / 2, height * 0.44);
  }, [height]);

  useEffect(() => {
    initCanvas();
    const ro = new ResizeObserver(initCanvas);
    if (canvasRef.current) ro.observe(canvasRef.current);
    return () => ro.disconnect();
  }, [initCanvas]);

  const getXY = useCallback((e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - rect.left, y: src.clientY - rect.top };
  }, []);

  // Attach non-passive touch listeners directly to canvas for mobile support
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const handleTouchStart = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (confirmedRef.current) return;
      isDrawingRef.current = true;
      hasDrawnRef.current = true;
      setIsDrawing(true);
      setHasDrawn(true);
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      lastPos.current = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
      const ctx = canvas.getContext('2d');
      ctx.beginPath();
      ctx.arc(lastPos.current.x, lastPos.current.y, 1.2, 0, Math.PI * 2);
      ctx.fillStyle = '#0a1a3a';
      ctx.fill();
    };

    const handleTouchMove = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!isDrawingRef.current || confirmedRef.current) return;
      const rect = canvas.getBoundingClientRect();
      const touch = e.touches[0];
      const pos = { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#0a1a3a';
      ctx.lineWidth = 2.2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.beginPath();
      ctx.moveTo(lastPos.current.x, lastPos.current.y);
      ctx.lineTo(pos.x, pos.y);
      ctx.stroke();
      lastPos.current = pos;
    };

    const handleTouchEnd = (e) => {
      e.preventDefault();
      isDrawingRef.current = false;
      setIsDrawing(false);
      lastPos.current = null;
      if (hasDrawnRef.current && onSignatureChange) {
        onSignatureChange(canvas.toDataURL('image/png'));
      }
    };

    // Add as NON-passive so preventDefault() actually works on mobile
    canvas.addEventListener('touchstart', handleTouchStart, { passive: false });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false });
    canvas.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onSignatureChange]);

  const startDraw = (e) => {
    if (confirmed) return;
    setIsDrawing(true);
    setHasDrawn(true);
    lastPos.current = getXY(e);
    const ctx = canvasRef.current.getContext('2d');
    const { x, y } = lastPos.current;
    ctx.beginPath();
    ctx.arc(x, y, 1.2, 0, Math.PI * 2);
    ctx.fillStyle = '#0a1a3a';
    ctx.fill();
  };

  const draw = (e) => {
    if (!isDrawing || confirmed) return;
    const ctx = canvasRef.current.getContext('2d');
    const pos = getXY(e);
    ctx.strokeStyle = '#0a1a3a';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
  };

  const endDraw = () => {
    setIsDrawing(false);
    lastPos.current = null;
    if (hasDrawn && onSignatureChange) {
      onSignatureChange(canvasRef.current.toDataURL('image/png'));
    }
  };

  const clear = () => {
    setHasDrawn(false);
    setConfirmed(false);
    if (onSignatureChange) onSignatureChange(null);
    initCanvas();
  };

  const confirm = () => {
    if (!hasDrawn) return;
    setConfirmed(true);
    if (onSignatureChange) onSignatureChange(canvasRef.current.toDataURL('image/png'));
  };

  const borderColor = confirmed ? '#2ee59d' : hasDrawn ? '#ff6b00' : 'rgba(192,192,192,0.3)';

  return (
    <div>
      {label && (
        <div className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.5rem' }}>
          <PenLine size={13} />
          {label}
          {required && <span style={{ color: 'var(--danger)' }}>*</span>}
        </div>
      )}
      <div style={{ border: `2px solid ${borderColor}`, borderRadius: 8, overflow: 'hidden', background: '#fff', cursor: confirmed ? 'default' : 'crosshair', position: 'relative', touchAction: 'none' }}>
        <canvas
          ref={canvasRef}
          style={{ display: 'block', width: '100%', touchAction: 'none' }}
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          data-testid="signature-canvas"
        />
        {confirmed && (
          <div style={{ position: 'absolute', top: 8, right: 8, background: '#2ee59d', borderRadius: '50%', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Check size={13} color="white" />
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
        <button type="button" onClick={clear} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'transparent', border: '1px solid rgba(192,192,192,0.25)', color: 'var(--text-muted)', borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.78rem', cursor: 'pointer' }} data-testid="signature-clear-btn">
          <Trash2 size={11} /> Clear
        </button>
        {!confirmed && hasDrawn && (
          <motion.button type="button" onClick={confirm} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', background: 'var(--neon-orange)', border: 'none', color: '#0a0e1a', borderRadius: 6, padding: '0.3rem 0.7rem', fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer' }} data-testid="signature-confirm-btn">
            <Check size={11} /> Confirm Signature
          </motion.button>
        )}
        {confirmed && <span style={{ color: 'var(--success)', fontSize: '0.78rem', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.3rem' }}><Check size={12} /> Confirmed</span>}
      </div>
    </div>
  );
};

export default SignatureCanvas;
